'use client';

/**
 * ContributeGuard — client-side gate for the user-world /contribute console.
 *
 * Mirrors src/components/admin/ProtectedRoute.tsx, but for the USER world:
 *   - ALLOWS anyone logged in (contributor / member / editor / admin / owner — any valid
 *     role). The console is the contributor's home; staff can view it too.
 *   - Logged-OUT users see an inline SIGN-IN affordance (Twitter sign-in), NOT a redirect.
 *   - It NEVER sends a contributor to /admin. /admin is the staff world; the two are
 *     deliberately separate (see WS-K auth plumbing).
 *
 * Defense-in-depth only: like ProtectedRoute, this is a CLIENT gate. The real
 * authorization for any write lives in the signal/answer API routes (requireRole +
 * verifyAndGetUser server-side). This guard just shapes the UX.
 *
 * NOTE on isActive: a brand-new contributor starts isActive:false (the staff-approval
 * flag). The contribute console intentionally does NOT require isActive — a logged-in
 * contributor can reach their console immediately. Per-action gating (what a not-yet-
 * verified contributor may actually submit) is enforced server-side by the API the
 * other agent builds, not here.
 */

import React from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface ContributeGuardProps {
  children: React.ReactNode;
  /**
   * Sign-in affordance copy (shown to logged-OUT users). Each defaults to the CONTRIBUTOR
   * console framing — so /you and /contribute, which pass nothing, render unchanged. /member
   * overrides them with verified-OWNER framing (it's a different console).
   */
  signInEyebrow?: string;
  signInTitle?: string;
  signInBody?: string;
  /**
   * Promote the sign-in CTA to /join's solid-amber weight (default: ghost/outline). Opt-in so
   * ONLY the WSK-gated console doors (/contribute, /member) get the stronger CTA — /you, which
   * renders this guard with no WSK gate, keeps the ghost button and stays flag-off byte-identical.
   */
  signInCtaSolid?: boolean;
  /**
   * Extra persona pitch rendered under the sub-copy (shown to logged-OUT users). /member passes
   * the dossier owner-card sell so the console door actually sells; /you and /contribute pass none.
   */
  signInPitch?: React.ReactNode;
  /**
   * A DIMMED, non-interactive preview of the real console rendered BELOW the CTA — the door shows
   * the room behind it. Opt-in: /contribute passes a queue of open questions + a would-be credit
   * row; /member passes a mock owner console. Decorative only (aria-hidden, pointer-events-none).
   *
   * DEFAULT-PRESERVING (the /you tripwire): undefined → nothing renders AND the live single-question
   * client peek still runs, so /you's ungated flag-off HTML stays byte-identical. When a preview IS
   * provided it SUPERSEDES that peek (we skip the fetch — never render both).
   */
  signInPreview?: React.ReactNode;
}

const DEFAULT_SIGN_IN_EYEBROW = 'Contributor console';
const DEFAULT_SIGN_IN_TITLE = 'Sign in to contribute';
const DEFAULT_SIGN_IN_BODY =
  'Contributors feed the entity graph with signals and answers. Sign in with X to open your console.';

function LoadingState() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-scout-gold" />
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.16em] text-parchment-dim">
          Loading…
        </p>
      </div>
    </div>
  );
}

function SignInAffordance({
  eyebrow = DEFAULT_SIGN_IN_EYEBROW,
  title = DEFAULT_SIGN_IN_TITLE,
  body = DEFAULT_SIGN_IN_BODY,
  ctaSolid = false,
  pitch,
  preview,
}: {
  eyebrow?: string;
  title?: string;
  body?: string;
  ctaSolid?: boolean;
  pitch?: React.ReactNode;
  preview?: React.ReactNode;
}) {
  const { signInWithTwitter, redirectSignInError } = useAuth();
  const [error, setError] = React.useState('');
  const [pending, setPending] = React.useState(false);
  // A real peek behind the gate: ONE open question from the public board. Fail-silent — any
  // error / empty board degrades to the plain wall (peek stays null). SUPERSEDED by `preview`
  // when a full console preview is supplied (we skip the fetch — never render both).
  const [peek, setPeek] = React.useState<string | null>(null);
  const hasPreview = preview != null;

  React.useEffect(() => {
    if (hasPreview) return; // preview supersedes the single-question peek — don't fetch both
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/public/open-questions?limit=1');
        if (!res.ok) return;
        const data = (await res.json()) as { openQuestions?: Array<{ question?: string }> };
        const q = data.openQuestions?.[0]?.question;
        if (!cancelled && typeof q === 'string' && q.trim()) setPeek(q);
      } catch {
        // fail-silent — fall back to the plain sign-in wall
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasPreview]);

  const handleSignIn = async () => {
    setError('');
    setPending(true);
    try {
      await signInWithTwitter();
    } catch (e) {
      setError('Sign-in failed. Please try again.');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <p className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-scout-gold">
        {eyebrow}
      </p>
      <h1 className="mb-4 font-display text-2xl font-black tracking-tight text-parchment sm:text-3xl">
        {title}
      </h1>
      <p className="mb-6 font-body text-[15px] leading-relaxed text-parchment-dim">
        {body}
      </p>
      {pitch}
      {peek && (
        <div
          data-testid="sign-in-peek"
          className="mx-auto mb-8 max-w-md rounded-md border-[0.5px] border-divider bg-surface2 px-4 py-3 text-left"
        >
          <p className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-scout-gold">
            Open on the record
          </p>
          <p className="font-body text-[13px] leading-snug text-parchment">{peek}</p>
          <p className="mt-2 font-body text-[12px] leading-relaxed text-parchment-dim">
            Answers that hold up are credited on the record — recognition, not money.
          </p>
        </div>
      )}
      <button
        type="button"
        onClick={() => handleSignIn()}
        disabled={pending}
        className={
          ctaSolid
            ? 'inline-flex items-center gap-2 rounded-md bg-scout-gold px-5 py-2.5 font-mono text-[13px] font-semibold uppercase tracking-[0.12em] text-ink transition hover:bg-scout-gold/90 disabled:cursor-not-allowed disabled:opacity-60'
            : 'inline-flex items-center gap-2 rounded-md border border-scout-gold/40 bg-scout-gold/10 px-5 py-2.5 font-mono text-[13px] font-semibold uppercase tracking-[0.12em] text-scout-gold transition hover:bg-scout-gold/20 disabled:cursor-not-allowed disabled:opacity-60'
        }
      >
        {pending ? 'Signing in…' : 'Sign in with X'}
      </button>
      {/* Local click error takes priority; otherwise surface a failed mobile redirect-return
          (a redirect that came back but couldn't complete) so the user isn't silently stranded. */}
      {(error || redirectSignInError) && (
        <p className="mt-4 font-body text-[13px] text-red-400">{error || redirectSignInError}</p>
      )}
      {/* The room behind the door — a DIMMED, non-interactive preview of the real console. Purely
          decorative (aria-hidden, no tab stops, unselectable). A bottom fade so it reads as "there's
          more below the fold", not a broken page. Never present on /you (default undefined).
          `inert` drops the preview's focusable descendants (QuestionCard links / <summary>,
          FreeTierCard's link) out of the tab order — aria-hidden alone leaves phantom keyboard
          focus (WCAG 4.1.2). React 18/19 mismatch: @types/react 18.3 types `inert` as boolean,
          but the React 18 RUNTIME treats it as an unknown attribute — a boolean true is warned
          about and DROPPED, while a string renders verbatim. So we pass the presence-based ''
          string through the boolean type (the standard React-18 workaround; renders inert=""). */}
      {hasPreview && (
        <div className="mx-auto mt-14 max-w-md text-left">
          <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-parchment-dim/70">
            A look at the console behind this door
          </p>
          <div
            data-testid="sign-in-preview"
            aria-hidden="true"
            inert={'' as unknown as boolean}
            className="pointer-events-none select-none opacity-50"
            style={{
              WebkitMaskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
              maskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
            }}
          >
            {preview}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ContributeGuard({
  children,
  signInEyebrow,
  signInTitle,
  signInBody,
  signInCtaSolid,
  signInPitch,
  signInPreview,
}: ContributeGuardProps) {
  const { userData, loading } = useAuth();

  // DEV ONLY — local dogfood bypass. Renders the console without a real Twitter login
  // so the contribute loop is demonstrable/screenshottable on local fixtures. HARD-GATED
  // on BOTH a NEXT_PUBLIC opt-in flag AND NODE_ENV !== 'production' so it is impossible in
  // a production build. Mirrors the answer-API's USE_LOCAL_FIXTURES dev shim. The real
  // authorization for every write still lives server-side in the answer API route.
  const devBypass =
    process.env.NEXT_PUBLIC_CONTRIBUTE_DEV_BYPASS === '1' &&
    process.env.NODE_ENV !== 'production';

  if (loading && !devBypass) {
    return <LoadingState />;
  }

  if (devBypass) {
    return (
      <>
        <p className="mb-4 rounded border-[0.5px] border-scout-gold/40 bg-scout-gold-dim px-3 py-1.5 text-center font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-scout-gold">
          Dev preview — local fixtures (not a real login)
        </p>
        {children}
      </>
    );
  }

  // Logged OUT (or no user-world doc) → inline sign-in affordance. NEVER redirect to /admin.
  if (!userData) {
    return (
      <SignInAffordance
        eyebrow={signInEyebrow}
        title={signInTitle}
        body={signInBody}
        ctaSolid={signInCtaSolid}
        pitch={signInPitch}
        preview={signInPreview}
      />
    );
  }

  // Logged IN with any valid role → allowed. Defense-in-depth: real authorization for
  // writes is enforced server-side in the signal/answer API routes.
  return <>{children}</>;
}
